import { z } from "zod";

export type Result<Ok, Err> = { ok: Ok } | { err: Err };

export function ok<Ok>(ok: Ok) {
	return { ok };
}

export function err<Err>(err: Err) {
	return { err };
}

export function isOk<Ok, Err>(res: Result<Ok, Err>): res is { ok: Ok } {
	return "ok" in res;
}

export function isErr<Ok, Err>(res: Result<Ok, Err>): res is { err: Err } {
	return "err" in res;
}

export function unwrapOr<Ok, Err>(res: Result<Ok, Err>, fallback: Ok): Ok {
	return isOk(res) ? res.ok : fallback;
}

/* API */

interface ErrorResponse {
	code: string;
	error: unknown;
}

const ERROR_CODES = {
	"Bad Request": 400,
	"Not Found": 404,
	"Too Many Requests": 429,
	"Internal Server Error": 500,
} as const;

export const badRequestSchema = z.object({
	status: z.literal(ERROR_CODES["Bad Request"]),
	error: z.object({
		code: z.string(),
		message: z.string(),
	}),
});

export const tooManyRequestsSchema = z.object({
	status: z.literal(ERROR_CODES["Too Many Requests"]),
	error: z.object({
		code: z.string(),
		message: z.string(),
	}),
});

export interface ErrorWithStatus {
	status: 400 | 404 | 429 | 500;
	error: ErrorResponse;
}

export type ApiResult<T> = Promise<Result<T, ErrorWithStatus>>;

type ErrorCode = keyof typeof ERROR_CODES;

export function apiErr(
	code: ErrorCode,
	error: unknown,
): {
	ok?: never;
	err: ErrorWithStatus;
} {
	return { err: { status: ERROR_CODES[code], error: { code, error } } };
}
